import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { AuthCajeroService } from './auth-cajero.service';
import { AuthRepartidorService } from './auth-repartidor.service';
import { AuthVendedorService } from './auth-vendedor.service';
import { environment } from '../environment/environment';

export const authInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const authCajeroService = inject(AuthCajeroService);
  const authRepartidorService = inject(AuthRepartidorService);
  const authVendedorService = inject(AuthVendedorService);
  const router = inject(Router);

  // Solo adjuntar token a requests dirigidos a nuestra API
  const isOwnApi = request.url.startsWith(environment.apiUrl);

  // Si el request no va a nuestra API, no adjuntar token ni manejar 401
  if (!isOwnApi) {
    return next(request);
  }

  // Detectar si es un endpoint público de la tienda (no requiere autenticación)
  const isPublicApiRequest = request.url.includes('/api/tienda/');

  // Si es un request público de la tienda, no añadir token ni manejar 401
  if (isPublicApiRequest) {
    return next(request);
  }

  // Detectar si estamos en el módulo cajero, repartidor o vendedor
  const isCajeroRoute = router.url.startsWith('/caja');
  const isRepartidorRoute = router.url.startsWith('/repartidor');
  const isVendedorRoute = router.url.startsWith('/vendedor');

  let token: string | null = null;

  if (isCajeroRoute) {
    // Usar token de cajero
    token = authCajeroService.token;
  } else if (isRepartidorRoute) {
    // Usar token de repartidor
    token = authRepartidorService.token;
  } else if (isVendedorRoute) {
    // Usar token de vendedor
    token = authVendedorService.token;
  } else {
    // Usar token de admin/usuario normal
    token = authService.getToken();
  }

  // Si hay un token, lo añadimos al encabezado
  if (token) {
    request = request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  // No re-disparar logout si el request que falló ya es un endpoint de logout:
  // provoca loop infinito (401 → logout → POST /logout → 401 → ...).
  const isLogoutRequest = /\/logout(-\w+)?$/.test(request.url);

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !isLogoutRequest) {
        if (isCajeroRoute) {
          authCajeroService.logout();
          router.navigate(['/caja/login']);
        } else if (isRepartidorRoute) {
          authRepartidorService.logout();
          router.navigate(['/repartidor/login']);
        } else if (isVendedorRoute) {
          authVendedorService.logout();
          router.navigate(['/vendedor/login']);
        } else {
          authService.logout();
          router.navigate(['/login']);
        }
      }
      return throwError(() => error);
    })
  );
};